/**
 * ShellModals - the shell's dialogs, in the two runs the page renders them in.
 *
 * `ShellTableModals`: hotkeys, settings, the GM's permissions (which opens
 * the vox groups dialog), the handout lightbox, the vox groups dialog (GM,
 * in a session) and the weapon editor. `ShellRosterModals`: the app's own
 * dialog, the character editor, the spawn and replace dialogs, and the
 * session file's export and import. The roster bar's context menu sits
 * between the two runs, so they stay two components and the DOM order
 * stays what it was. Each renders only what is open. Extracted from
 * DeathWatchSimulator; the markup is unchanged.
 */
import { DEFAULT_LOCKS } from '../../engine/constants';
import { choicesWithLeaders, teamsIn } from '../../engine/roster/team';
import { spreadAtTable } from './spreadAtTable';
import { lazy, Suspense } from 'react';
import HotkeysModal from '../HotkeysModal.jsx';
import LazyWhenShown from '../LazyWhenShown.jsx';
import AppDialog from '../AppDialog.jsx';
import SpawnDialog from '../SpawnDialog';
import ReplaceDialog from '../ReplaceDialog';
import ExportDialog from '../ExportDialog';
import ImportDialog from '../ImportDialog';
import HandoutLightbox from '../HandoutLightbox';
import ChannelsDialog from '../ChannelsDialog';

// The rarely opened, loaded on first use (the perf overhaul): out of the bundle every table compiles before it draws.
const PermissionsModal = lazy(() => import('../PermissionsModal.jsx'));
const SettingsModal = lazy(() => import('../SettingsModal.jsx'));
const RackBench = lazy(() => import('../weaponEditor/RackBench'));
const CharacterEditor = lazy(() => import('../CharacterEditor'));

/** The vox groups dialog, for the GM of a session with a vox. */
function VoxGroupsDialog({ vox, show, onClose, roster }) {
  const { channelsApi } = vox;
  return (
    <ChannelsDialog
      show={show}
      onClose={onClose}
      channels={channelsApi.channels}
      people={vox.voxPeople}
      teams={teamsIn(roster)}
      actingBy={vox.actingBy}
      onCreate={channelsApi.create}
      onUpdate={channelsApi.update}
      onRemove={channelsApi.remove}
      onSeed={channelsApi.seedFromTeam}
    />
  );
}

/** Hotkeys, settings, permissions, the lightbox, the vox groups and the weapon editor. */
export function ShellTableModals(p) {
  const { sessionId, isGm, permissions } = p;
  return (
    <>
      <HotkeysModal show={p.showHotkeys} onClose={() => p.setShowHotkeys(false)} />
      <LazyWhenShown
        component={SettingsModal}
        show={p.showSettings}
        onClose={() => p.setShowSettings(false)}
        sessionId={sessionId}
        permissions={permissions}
        canEditSession={!!sessionId && isGm}
      />
      <LazyWhenShown
        component={PermissionsModal}
        show={p.showPermPanel && isGm}
        onClose={() => p.setShowPermPanel(false)}
        sessionId={sessionId}
        permissions={permissions}
        connectedPlayers={p.connectedPlayers}
        DEFAULT_LOCKS={DEFAULT_LOCKS}
        onOpenChannels={() => {
          p.setShowPermPanel(false);
          p.setShowChannels(true);
        }}
      />
      {p.viewedHandout && (
        <HandoutLightbox
          handout={p.viewedHandout}
          resolve={p.handoutFileUrl}
          presented={p.handoutPresented}
          onEndPresent={isGm ? () => p.endHandoutPresent(p.viewedHandout.id) : undefined}
          onClose={p.closeHandoutView}
        />
      )}
      {p.chatContextValue && isGm && (
        <VoxGroupsDialog vox={p.vox} show={p.showChannels} onClose={() => p.setShowChannels(false)} roster={p.roster} />
      )}
      {/* The one weapon editor (plan armoury): the rack's door. */}
      {p.editingSlot !== null && (
        <Suspense fallback={null}>
          <RackBench index={p.editingSlot} p={p} />
        </Suspense>
      )}
    </>
  );
}

/** The session file's export dialog, counting what the file would carry. */
function SessionExport({ p }) {
  const { roster, terrain, library, tokens, waiting } = p;
  return (
    <ExportDialog
      combatantCount={roster.length}
      characterIds={roster.filter((c) => c.kind === 'character').map((c) => c.id)}
      isGm={p.isGm}
      sheetsLocked={p.isLocked('editSheets')}
      loadoutCount={p.loadouts.length}
      hasTerrain={!!terrain}
      mapCount={library.length}
      tokenCount={Object.keys(tokens).length}
      roomCount={waiting.rooms.length}
      handoutCount={p.handoutCount}
      tabs={p.tabs}
      atkProfiles={p.atkProfiles}
      tgtTemplates={p.tgtTemplates}
      onExport={p.handleExportConfirm}
      onCancel={p.handleExportCancel}
    />
  );
}

/**
 * The teams the editor offers to join: the roster's and the library's, never the sheet being edited (by its id, which
 * its roster copies keep) - its own team is edited in place, and counting it would make it look shared.
 */
function teamsToJoin(roster, library, editing) {
  // Each sheet ONCE, by id: a library character and his roster copies are one person (the library's record first).
  const byId = new Map();
  for (const sheet of [...library, ...roster.map((c) => c.sheet)]) if (!byId.has(sheet.id)) byId.set(sheet.id, sheet);
  byId.delete(editing?.id);
  return choicesWithLeaders([...byId.values()]);
}

/** The character editor, saving into the active library; a kill-team change reaches the rest of its team. */
function CharacterEditorDialog({ p }) {
  return (
    <CharacterEditor
      character={p.editingCharacter}
      onSave={(c) => {
        p.activeLibrary.saveCharacter(c);
        // The GM's save carries a kill-team change to the rest of the team; a player's stays on his own sheet.
        if (p.isGm) {
          const table = { roster: p.roster, library: p.activeLibrary, updateCombatantSheet: p.updateCombatantSheet };
          spreadAtTable(table, p.editingCharacter, c);
        }
        p.setShowCharacterEditor(false);
      }}
      onCancel={() => p.setShowCharacterEditor(false)}
      allowCrossTypeSpecials={p.allowCrossTypeSpecials}
      onToggleCrossTypeSpecials={p.onToggleCrossTypeSpecials}
      canEditProtagonist={!p.isLocked('protagonistTab')}
      // Join a team on the roster or in the library (2026-10-23): the tag is never typed again.
      teams={teamsToJoin(p.roster, p.activeLibrary.characters, p.editingCharacter)}
      tactics={p.libraryTactics}
      bundles={p.libraryBundles}
      spreadsTeam={p.isGm}
    />
  );
}

/** The app's dialog, the character editor, spawn, replace, export and import. */
export function ShellRosterModals(p) {
  const { dialog, spawnDialog, replaceOutgoing, importDialogData } = p;
  return (
    <>
      {dialog && <AppDialog dialog={dialog} onClose={p.handleDialogClose} />}
      {p.showCharacterEditor && (
        <Suspense fallback={null}>
          <CharacterEditorDialog p={p} />
        </Suspense>
      )}
      {spawnDialog && (
        <SpawnDialog
          character={spawnDialog.character}
          onConfirm={p.handleSpawnConfirm}
          onCancel={() => p.setSpawnDialog(null)}
          canHide={p.canHide}
        />
      )}
      {replaceOutgoing && (
        <ReplaceDialog
          outgoing={replaceOutgoing}
          rooms={p.waiting.rooms}
          library={p.activeLibrary.characters}
          refusal={p.replaceEarlyRefusal}
          onConfirm={p.replaceFromDialog}
          onCancel={() => p.setReplaceDialog(null)}
        />
      )}
      {p.showExportDialog && <SessionExport p={p} />}
      {importDialogData && (
        <ImportDialog data={importDialogData} onImport={p.handleImportConfirm} onCancel={p.handleImportCancel} />
      )}
    </>
  );
}
